import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription, SubscriptionAPI } from 'dva';
import { DvaReducer } from './shared';
import { AVATAR_URL_PREFIX } from '@/constants';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI } from '@freelog/tools-lib';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import dayjs from 'dayjs';

type HandledOperationCategories = {
  id: string;
  code: string;
  name: string;
  parentID: string;
  depth: number;
}[];


export interface DiscoverPageModelState {
  showPage: 'market' | 'example';
  resourceTypeOptions: {
    text: string;
    value: string;
  }[];

  resourceType: string;
  selectedOperationCategoryIDs: string[];
  filterSelected: '4,5' | '5' | '3,4,5';
  inputText: string;
  dataSource: {
    id: string;
    cover: string;
    name: string;
    title: string;
    version: string;
    policy: string[];
    type: string[];
    status: 0 | 1 | 2 | 4;
    authProblem?: boolean;
    updateDate: string;
    username: string;
    useAvatar: string;
    isChoice: boolean;
    collection?: {
      status: 'finish' | 'unFinish';
      count: number;
    } | null;
  }[];
  tags: string;
  totalItem: number;
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'discoverPage/change';
  payload: Partial<DiscoverPageModelState>;
}

export interface OnChange_ShowPage_Action extends AnyAction {
  type: 'discoverPage/onChange_ShowPage';
  payload: {
    value: 'market' | 'example';
  };
}

export interface OnMountPageAction extends AnyAction {
  type: 'discoverPage/onMountPage';
}

export interface OnUnmountPageAction extends AnyAction {
  type: 'discoverPage/onUnmountPage';
}

export interface OnMountMarketPageAction extends AnyAction {
  type: 'discoverPage/onMountMarketPage';
}

export interface OnUnmountMarketPageAction extends AnyAction {
  type: 'discoverPage/onUnmountMarketPage';
}

export interface OnMountExamplesPageAction extends AnyAction {
  type: 'discoverPage/onMountExamplesPage';
}

export interface FetchDataSourceAction extends AnyAction {
  type: 'fetchDataSource';
  payload: {
    restart: boolean;
  };
}

export interface OnChangeResourceTypeAction extends AnyAction {
  type: 'discoverPage/onChangeResourceType';
  payload: {
    value: string;
  };
}

export interface OnChange_SelectedOperationCategoryIDs_Action extends AnyAction {
  type: 'discoverPage/onChange_SelectedOperationCategoryIDs';
  payload: {
    value: string[]
  };
}

export interface OnChange_FilterSelected_Action extends AnyAction {
  type: 'discoverPage/onChange_FilterSelected';
  payload: {
    // 4,5 首页推荐(评级≥4)；5 编辑精选；3,4,5 最新发布
    value: '4,5' | '5' | '3,4,5';
  };
}

export interface OnChangeKeywordsAction extends AnyAction {
  type: 'discoverPage/onChangeKeywords';
  payload: {
    value: string;
  };
}

export interface OnChangeTagsAction extends AnyAction {
  type: 'discoverPage/onChangeTags';
  payload: {
    value: string;
  };
}

export interface OnClickLoadMoreBtnAction extends AnyAction {
  type: 'discoverPage/onClickLoadMoreBtn';
}

export interface DiscoverPageModelType {
  namespace: 'discoverPage';
  state: DiscoverPageModelState;
  effects: {
    onChange_ShowPage: (action: OnChange_ShowPage_Action, effects: EffectsCommandMap) => void;
    onMountPage: (action: OnMountPageAction, effects: EffectsCommandMap) => void;
    onUnmountPage: (action: OnUnmountPageAction, effects: EffectsCommandMap) => void;
    onMountMarketPage: (action: OnMountMarketPageAction, effects: EffectsCommandMap) => void;
    onUnmountMarketPage: (action: OnUnmountMarketPageAction, effects: EffectsCommandMap) => void;
    fetchDataSource: (action: FetchDataSourceAction, effects: EffectsCommandMap) => void;

    onChange_SelectedOperationCategoryIDs: (action: OnChange_SelectedOperationCategoryIDs_Action, effects: EffectsCommandMap) => void;
    onChangeResourceType: (action: OnChangeResourceTypeAction, effects: EffectsCommandMap) => void;
    onChange_FilterSelected: (action: OnChange_FilterSelected_Action, effects: EffectsCommandMap) => void;
    onChangeKeywords: (action: OnChangeKeywordsAction, effects: EffectsCommandMap) => void;
    onChangeTags: (action: OnChangeTagsAction, effects: EffectsCommandMap) => void;
    onClickLoadMoreBtn: (action: OnClickLoadMoreBtnAction, effects: EffectsCommandMap) => void;
  };
  reducers: {
    change: DvaReducer<DiscoverPageModelState, ChangeAction>;
  };
  subscriptions: {
    setup: Subscription;
  };
}

const marketInitStates: Pick<DiscoverPageModelState,
  'resourceTypeOptions' |
  'resourceType' |
  'selectedOperationCategoryIDs' |
  'filterSelected' |
  'inputText' |
  'dataSource' |
  'totalItem' |
  'tags'> = {
  resourceTypeOptions: [
    {
      value: '-1',
      text: '全部类型',
    },
    ...FUtil.Predefined.resourceTypes.map((i) => ({ value: i, text: i })),
  ],
  resourceType: '-1',
  selectedOperationCategoryIDs: ['#all'],
  filterSelected: '4,5',
  inputText: '',
  tags: '',
  dataSource: [],
  totalItem: -1,
};

export const initStates: DiscoverPageModelState = {
  showPage: 'market',

  ...marketInitStates,
};

const Model: DiscoverPageModelType = {
  namespace: 'discoverPage',

  state: initStates,

  effects: {
    * onChange_ShowPage({ payload }: OnChange_ShowPage_Action, { put }: EffectsCommandMap) {
      // console.log(payload, 'payload09io3j2lksdjflkj')
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          showPage: payload.value,
        },
      });
    },
    * onMountPage({}: OnMountPageAction, { put }: EffectsCommandMap) {
    },
    * onUnmountPage({}: OnUnmountPageAction, { put }: EffectsCommandMap) {
    },
    * onMountMarketPage({}: OnMountMarketPageAction, { call, put }: EffectsCommandMap) {
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    * onUnmountMarketPage({}: OnUnmountMarketPageAction, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: marketInitStates,
      });
    },
    * fetchDataSource(
      { payload }: FetchDataSourceAction,
      { call, put, select, take }: EffectsCommandMap,
    ) {
      const { discoverPage }: ConnectState = yield select(({ discoverPage }: ConnectState) => ({
        discoverPage,
      }));

      let dataSource: DiscoverPageModelState['dataSource'] = [];

      if (!payload.restart) {
        dataSource = discoverPage.dataSource;
      }

      const existentResourceIDs: string[] = dataSource.map((ds) => {
        return ds.id;
      });

      const selectedOperationCategoryIDs: string[] = discoverPage.selectedOperationCategoryIDs.filter((c) => {
        return c !== '#all';
      });

      const params: Parameters<typeof FServiceAPI.Resource.list>[0] = {
        skip: dataSource.length,
        limit: FUtil.Predefined.pageSize,
        // startResourceId: dataSource[0]?.id,
        keywords: discoverPage.inputText,
        resourceType: discoverPage.resourceType === '-1' ? undefined : discoverPage.resourceType,
        status: 1,
        operationCategoryCode: selectedOperationCategoryIDs[selectedOperationCategoryIDs.length - 1],
        // operationCategoryCode: selectedOperationCategoryIDs.at(-1),
        operationTypes: discoverPage.filterSelected,
      };

      const { data }: {
        data: {
          dataList: {
            resourceId: string;
            coverImages: string[];
            resourceName: string;
            resourceTitle: string;
            resourceType: string[];
            updateDate: string;
            status: 0 | 1 | 2 | 4;
            latestVersion: string;
            policies: PolicyFullInfo_Type[];
            // updateDate: string;
            username: string;
            userId: number;
            operationType: 0 | 1 | 2 | 3 | 4 | 5;
            serializeStatus: 0 | 1;
            subjectType: 1 | 4;
            resourceVersions: {
              versionId: string;
            }[];
          }[];
          totalItem: number;
        };
      } = yield call(FServiceAPI.Resource.list, params);

      const params2: Parameters<typeof FServiceAPI.Resource.getVersionList>[0] = {
        versionIds: data.dataList.filter((bi) => {
          return bi.subjectType === 4;
        }).map((bi) => {
          return bi.resourceVersions[0].versionId;
        }).join(','),
      };
      const { data: data_versionList }: {
        data: {
          resourceId: string;
          catalogueProperty: { collection_item_count: number };
        }[];
      } = yield call(FServiceAPI.Resource.getVersionList, params2);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          totalItem: data.totalItem,
          dataSource: [
            ...dataSource,
            ...data.dataList
              .filter((i) => {
                return !existentResourceIDs.includes(i.resourceId);
              })
              .map<DiscoverPageModelState['dataSource'][number]>((i) => {
                // console.log(i, 'i#@@#$@#$@#$@#$@#4234098ijosfdlksd');
                const resourceVersionInfo = data_versionList.find((vl) => {
                  return vl.resourceId === i.resourceId;
                });
                return {
                  id: i.resourceId,
                  cover: i.coverImages.length > 0 ? i.coverImages[0] : '',
                  name: i.resourceName,
                  title: i.resourceTitle,
                  version: i.latestVersion,
                  policy: i.policies
                    .filter((l) => {
                      return l.status === 1;
                    })
                    .map((l) => l.policyName),
                  type: i.resourceType,
                  status: 1,
                  updateDate: dayjs(i.updateDate).format(FUtil.Predefined.momentDateFormat),
                  username: i.username,
                  useAvatar: `${AVATAR_URL_PREFIX}${i.userId}`,
                  isChoice: i.operationType === 5,
                  collection: (i.subjectType === 4 && !!resourceVersionInfo)
                    ? {
                      status: i.serializeStatus === 1 ? 'finish' : 'unFinish',
                      count: resourceVersionInfo.catalogueProperty.collection_item_count,
                    }
                    : null,
                };
              }),
          ],
        },
      });
    },
    * onChange_SelectedOperationCategoryIDs({ payload }: OnChange_SelectedOperationCategoryIDs_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          selectedOperationCategoryIDs: payload.value,
        },
      });
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    * onChangeResourceType({ payload }: OnChangeResourceTypeAction, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceType: payload.value,
          tags: '',
        },
      });

      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    * onChange_FilterSelected({ payload }: OnChange_FilterSelected_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          filterSelected: payload.value,
        },
      });

      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    * onChangeKeywords({ payload }: OnChangeKeywordsAction, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          inputText: payload.value,
        },
      });

      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    * onChangeTags({ payload }: OnChangeTagsAction, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          tags: payload.value,
        },
      });

      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },

    * onClickLoadMoreBtn({}: OnClickLoadMoreBtnAction, { put }: EffectsCommandMap) {
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: false,
        },
      });
    },
  },

  reducers: {
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },

  subscriptions: {
    setup({ dispatch, history }: SubscriptionAPI) {
    },
  },
};

export default Model;

type OperationCategories = {
  children: OperationCategories;
  code: string;
  name: string;
}[];

function flatOperationCategories(operationCategories: OperationCategories, parentID: string, depth: number, payload: HandledOperationCategories) {
  if (operationCategories.length > 0) {
    payload.push({
      id: `${parentID}/#all`,
      code: '#all',
      name: '全部',
      parentID: parentID,
      depth: depth,
    });
  }
  for (const operationCategory of operationCategories) {
    const currentID: string = `${parentID}/${operationCategory.code}`;
    payload.push({
      id: currentID,
      code: operationCategory.code,
      name: operationCategory.name,
      parentID: parentID,
      depth: depth,
    });
    flatOperationCategories(operationCategory.children, currentID, depth + 1, payload);
  }
}
